import updatePreferredZipCode from 'services/api/profile/updatePreferredZipCode';
import getLocationWithCache from 'services/api/utility/getLocationWithCache';
import localeUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { TIME_OUT } from 'constants/location';
import cookieUtils from 'utils/Cookies';
import UserUtils from 'utils/User';

const UserLocation = {
    getBrowserGeoLocation: () => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        const locationObj = {
                            lat: position.coords.latitude,
                            lon: position.coords.longitude
                        };
                        resolve(locationObj);
                    },
                    error => reject(error),
                    { timeout: TIME_OUT }
                );
            }
        });
    },

    updatePreferredZipCode: async locationData => {
        const isUserAnonymous = UserUtils.isAnonymous();

        return new Promise((resolve, reject) => {
            updatePreferredZipCode(locationData)
                .then(async data => {
                    if (data.zipCode) {
                        if (!locationData.skipZipCodeUpdate) {
                            await UserLocation.finalizeZipCodeUpdate(data);
                        } else if (isUserAnonymous) {
                            cookieUtils.write('sameDayZipcodeCookie', data.zipCode, null, false, false);
                        }

                        resolve(data);
                    } else {
                        reject(data);
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    initZipCode: async () => {
        let parameters = { initZipCode: true };
        const userPreferredZipCode = UserUtils.getZipCode();
        const postalCode = userPreferredZipCode || Storage.session.getItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE)?.preferredZipCode;

        if (postalCode) {
            parameters = { ...parameters, postalCode };
        }

        return updatePreferredZipCode(parameters).then(async data => {
            await UserLocation.finalizeZipCodeUpdate(data);

            return data;
        });
    },

    updateZipCode: async (data = {}) => {
        const { lat, lon, zipCode, postalCode } = data;

        const parameters = {};

        if (lat) {
            parameters.lat = lat;
            parameters.lon = lon;
        }

        if (zipCode) {
            parameters.postalCode = zipCode;
        }

        if (postalCode) {
            parameters.postalCode = postalCode;
        }

        return updatePreferredZipCode(parameters).then(async response => {
            await UserLocation.finalizeZipCodeUpdate(response);

            return response;
        });
    },

    setPreferredZipCodeOnSession: (preferredZipCode, init) => {
        // If the user has a preferredZipCode, just send init
        // This is the path to set preferredZipcode:
        // https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=DEF&title=Same+Day+FE+Walkthrough
        if (preferredZipCode && init) {
            return UserLocation.initZipCode();
        } else {
            // If not, use user's geolocation to get a lat and lon, update the zipcode
            // with that information and finally send init
            return UserLocation.getBrowserGeoLocation()
                .then(data => {
                    return UserLocation.updateZipCode(data);
                })
                .catch(error => {
                    // If the user doesn't allow geolocation
                    return (
                        getLocationWithCache({ radius: localeUtils.getCountrySearchRadius() })
                            .then(locationData => {
                                const stores = locationData?.stores;
                                let postalCode;

                                if (stores) {
                                    postalCode = stores[0]?.address?.postalCode;
                                }

                                if (postalCode) {
                                    return UserLocation.updateZipCode({ postalCode });
                                } else {
                                    // If there are NO stores near the zipcode we got through Akamai
                                    return error;
                                }
                            })
                            // If unable to find location details
                            // eslint-disable-next-line no-console
                            .catch(errorInitZipCode => console.log(errorInitZipCode))
                    );
                });
        }
    },

    finalizeZipCodeUpdate: async data => {
        if (!data.zipCode) {
            return;
        }

        const [userActionsModule, storeModule] = await Promise.all([import('actions/UserActions'), import('store/Store')]);
        const userActions = userActionsModule.default;
        const store = storeModule.default;

        // Write sameDayZipcodeCookie cookie
        if (Sephora.configurationSettings.setZipStoreCookie) {
            cookieUtils.write('sameDayZipcodeCookie', data.zipCode, null, false, false);
        }

        // Write SAME_DAY_DELIVERY_AVAILABLE to local storage
        Storage.local.setItem(LOCAL_STORAGE.SAME_DAY_DELIVERY_AVAILABLE, data?.sameDayAvailable);

        // Update Redux state and session storage
        const zipData = {
            preferredZipCode: data.zipCode,
            encryptedStoreIds: data?.encryptedStoreIds ?? null
        };

        store.dispatch(userActions.update(zipData, false));
        Storage.session.setItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE, zipData);
    }
};

export default UserLocation;
